import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { activityAnchor } from '@molt/shared';
import type { ActivityFeed } from '@molt/shared';

import { Button, EmptyState, Notice, Skeleton } from '@/components';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import {
  boardActivityQueryOptions,
  getBoardActivity,
  mergeActivityFeeds,
  type ActivityFilter,
} from '@/lib/activity';
import { announce } from '@/lib/announce';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  absolutePermalink,
  copyTextToClipboard,
  getActivityPermalink,
  parsePermalinkHash,
  permalinkPath,
} from '@/lib/permalinks';
import { queryKeys } from '@/lib/query-keys';
import { useSession } from '@/lib/session';
import { ActivityEntryItem } from '../card-back/ActivityEntryItem';

const PAGE_SIZE = 25;

export interface BoardActivitySectionProps {
  boardId: string;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
}

/**
 * ⚠⚠ THE `enabled` GATE IS NOT A LOADING OPTIMISATION. WITHOUT IT AN ANONYMOUS
 * VISITOR IS THROWN OFF A PUBLIC BOARD, AND NOTHING ANYWHERE IS RED.
 * `FE-61.2`, `FR-COLLAB-012`, `FR-PERM-010` AC1.
 *
 * `GET /boards/:id/activity` is `requireUser` **even on a public board** —
 * B28's deliberate call, argued at length in `apps/server/src/routes/activity.ts`
 * (`FR-PERM-010` AC1 grants a signed-out visitor "lists, cards and comments"
 * and activity names every actor of every change). `main.tsx` binds a **global**
 * sign-in redirect to `isUnauthenticated`, so one ungated 401 from this section
 * navigates the visitor to `/sign-in?next=…`.
 *
 * That looks unreachable while signed out — the board menu's trigger is gated
 * on `signedIn` and `FR-PERM-010.spec.ts` asserts it is absent — but there is a
 * **second** way in that no gate covered: `routes/b.$shortLink…tsx` reads the
 * permalink hash and passes `initialBoardMenuSection: 'activity'` with **no
 * session conjunct**, so `#activity-<id>` opens this panel for anyone. Measured
 * before the fix: a signed-out visitor loading `/b/<shortLink>#activity-<ulid>`
 * on a public board drew the board, then landed on
 * `/sign-in?next=%2Fb%2F…%23activity-…`. The board face is exactly what
 * `FR-PERM-010` AC1 promises them, and a permalink took it away.
 *
 * `routes/b.$shortLink.{-$slug}.tsx` records the identical hazard for
 * `GET /boards/:id/members` — *"Fired while signed out, this query would
 * redirect the anonymous visitor to `/sign-in`, bouncing them off the very
 * public board `FR-PERM-010` exists to let them read. There is no error branch
 * here and there must not be one: the gate is the `enabled`."* Same rule, same
 * spelling, one surface later.
 *
 * ⚠ **Three conjuncts, never `profile !== null` alone.** `useSession` returns
 * `isPending: true` with `profile: null` on the first paint of **every** load,
 * signed in or not, so a one-conjunct gate flashes the sign-in state at a
 * member and can leave the query disabled. `w.$workspaceId.activity.tsx` is the
 * shape copied here.
 *
 * ⚠ **The signed-out branch renders BEFORE the error branch, and the order is
 * load-bearing.** With the query gated it never runs while signed out, so
 * `isError` is false and `data` is `undefined` — the old
 * `activity.isError || data === undefined` arm would answer the honest
 * signed-out case with a red "Activity could not be loaded", which is a
 * failure notice for a state that is not a failure.
 *
 * ⚠ **And its Retry would NOT be inert, which is worse than if it were.**
 * Measured in the vendored source rather than assumed: `@tanstack/query-core@5.101.4`
 * `queryObserver.js` implements `refetch()` as `fetch()` → `#executeFetch()` →
 * `query.fetch()` with **no `enabled` check anywhere on the path**, so a manual
 * refetch fires against a disabled query. Pressed by a signed-out visitor the
 * button would have issued the very 401 this gate exists to prevent.
 *
 * ⚠ **This narrows nothing that was ever served.** The feed answered 401 to an
 * anonymous caller before and answers 401 now; the only change is that the
 * client stops asking. Whether a single linked row may be shown where the whole
 * feed may not is `FR-COLLAB-012` against `FR-PERM-010` AC1 and is ruled on in
 * `docs/design/fe9-activity-permalink/AUDIT.md` — it stays closed here.
 *
 * ---------------------------------------------------------------------------
 * THE SIGNED-OUT ARM, AND WHY IT IS STILL REACHABLE AFTER THE ROUTE WAS FIXED
 * ---------------------------------------------------------------------------
 * Fan-out 13 closed the ejection at its source — `routes/b.$shortLink…tsx` now
 * computes `initialBoardMenuSection` with a `signedIn &&` conjunct, so the
 * permalink no longer opens `BoardMenuPanel` for a visitor with no session.
 * That makes the obvious question worth answering out loud: **is this arm now
 * dead code?**
 *
 * ⚠ **THE HONEST ANSWER IS "PROBABLY NOT, AND IT STAYS ANYWAY" — the stronger
 * claim was drafted here and withdrawn after it failed to reproduce.** What is
 * certain is the mechanism: `BoardFace` turns `initialBoardMenuSection` into
 * `setMenuOpen(true)` inside an effect and never re-derives `menuOpen` from
 * `signedIn`, so `open` means "signed in **when the panel opened**", never
 * "signed in now"; and `sessionQueryOptions` sets only `staleTime: 30_000`,
 * inheriting `refetchOnWindowFocus: true` from `main.tsx`'s defaults. So a
 * member whose session ends while the panel is open, whose tab is then
 * refocused after the stale window, gets a `/me` refetch that 401s and
 * `isSignedOut` flips to `true` under a still-mounted panel.
 *
 * ⚠ **That path was NOT reproduced.** Driven under Playwright — cookies
 * cleared, 33 seconds waited out, `visibilitychange` and `focus` dispatched
 * both inside and outside the stale window — the refetch never fired and the
 * arm never rendered. Synthetic focus does not move TanStack's `focusManager`.
 * So this paragraph describes a mechanism, not a measurement, and it is
 * written that way on purpose: a docstring that claimed a demonstrated state
 * would be the next chunk's false residual, which is exactly the defect this
 * chunk was sent to correct.
 *
 * What IS measured is that the arm renders correctly when it is reached —
 * `BoardActivitySection.test.tsx` mounts it with `/api/me` answering 401 and
 * pins the copy, the absent filter, and the absence of the error arm. Treat
 * this as defence in depth against a second door into the panel, of which
 * `#activity-…` was the first and was found only after it had shipped.
 *
 * ⚠ **AN OBSERVED RESIDUAL, DELIBERATELY NOT FIXED HERE.** The same latch
 * leaves `BoardMenuPanel`'s other `open`-gated queries live across that session
 * drop, and React Query refetches on focus and on reconnect — so a session that
 * ends while the panel is open can still produce one of the five 401s that
 * `main.tsx` turns into a `/sign-in` redirect. Closing that means widening
 * `enabled` inside `BoardMenuPanel.tsx`, which fan-out 13 did not own. It is
 * recorded in `docs/notes/fe-plan.md` rather than repaired quietly here.
 *
 * ⚠ **TWO KEYS, AND NO SIGN-IN BUTTON — FORCED, NOT PREFERRED.**
 * `w.$workspaceId.activity.tsx` ships the same state with a third key,
 * `workspaceSettings.activity.signInAction`, whose value is `'Sign in'`. That
 * value already has **seventeen** authors, and two pin files this chunk does
 * not own assert the author list EXACTLY — `workspace-settings.routing.test.ts`
 * (line 969) and `workspace-members.routing.test.ts` (line 770). An eighteenth
 * author turns both red, and repairing them means editing outside this chunk's
 * file list. Measured, not inherited: `signed-out.routing.test.ts` also asserts
 * `authorsOf('Sign in')`, but its local helper is scoped to `signedOut.*`, so
 * that one is unaffected — the residual named only the first file and was right
 * about it.
 *
 * The cost of leaving the button out is small and was checked: the shell's
 * banner renders a `Sign in` link on every page including this one, and
 * `FR-PERM-010.spec.ts` asserts it is visible to exactly this visitor. The
 * state explains itself and the affordance is one glance away.
 */
export function BoardActivitySection({
  boardId,
  onOpenCard,
}: BoardActivitySectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const { profile, isPending: sessionPending, isSignedOut } = useSession();
  const canLoadActivity = !isSignedOut && !sessionPending && profile !== null;
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [highlightedTarget, setHighlightedTarget] = useState<string | null>(null);
  const [locationHash, setLocationHash] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.hash,
  );
  const loadingPermalinkTarget = useRef(false);
  const activity = useQuery({
    ...boardActivityQueryOptions(boardId, filter, PAGE_SIZE),
    enabled: canLoadActivity,
  });
  const data = activity.data;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHash = () => setLocationHash(window.location.hash);
    window.addEventListener('hashchange', updateHash);
    return () => window.removeEventListener('hashchange', updateHash);
  }, []);

  useEffect(() => {
    if (data === undefined || typeof window === 'undefined') return undefined;
    const parsed = parsePermalinkHash(locationHash);
    if (parsed?.target !== 'activity') return undefined;

    const targetId = locationHash.startsWith('#') ? locationHash.slice(1) : locationHash;
    const matchingEntry = data.entries.find((entry) => entry.id === parsed.id);
    if (matchingEntry === undefined) {
      if (filter === 'comments') {
        setFilter('all');
        return undefined;
      }
      if (data.nextCursor !== null && !loadingPermalinkTarget.current) {
        let cancelled = false;
        loadingPermalinkTarget.current = true;
        void getBoardActivity(boardId, {
          filter,
          cursor: data.nextCursor,
          limit: PAGE_SIZE,
        })
          .then((next) => {
            if (cancelled) return;
            queryClient.setQueryData<ActivityFeed>(
              queryKeys.boards.activity(boardId, filter),
              (current) => mergeActivityFeeds(current, next),
            );
          })
          .finally(() => {
            if (!cancelled) loadingPermalinkTarget.current = false;
          });
        return () => {
          cancelled = true;
        };
      }
      return undefined;
    }

    setHighlightedTarget(targetId);
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView?.({
        block: 'center',
        behavior: 'smooth',
      });
    });
    const timer = window.setTimeout(() => setHighlightedTarget(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [boardId, data, filter, locationHash, queryClient]);

  async function loadMore() {
    if (data?.nextCursor === null || data?.nextCursor === undefined) return;
    const cursor = data.nextCursor;
    setLoadingMore(true);
    try {
      const next = await getBoardActivity(boardId, {
        filter,
        cursor,
        limit: PAGE_SIZE,
      });
      queryClient.setQueryData<ActivityFeed>(
        queryKeys.boards.activity(boardId, filter),
        (current) => {
          if (current !== undefined && current.nextCursor !== cursor) return current;
          return mergeActivityFeeds(current ?? data, next);
        },
      );
    } finally {
      setLoadingMore(false);
    }
  }

  async function copyActivityLink(activityId: string): Promise<void> {
    try {
      const response = await getActivityPermalink(activityId);
      const copied = await copyTextToClipboard(
        absolutePermalink(permalinkPath(t, response)),
      );
      announce(
        copied
          ? t('boardAdmin.activity.linkCopied')
          : t('boardAdmin.activity.linkCopyFailed'),
      );
    } catch {
      announce(t('boardAdmin.activity.linkCopyFailed'));
    }
  }

  return (
    <section
      aria-label={t('boardAdmin.activity.landmark')}
      className="flex flex-col gap-3"
    >
      {/* Hidden while signed out: a filter over a feed that will never load is
          a control that answers nothing. The gate is the same fact as the
          query's `enabled`, read once. */}
      {isSignedOut ? null : (
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="toggle"
            pressed={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            {t('boardAdmin.activity.filter.all')}
          </Button>
          <Button
            size="sm"
            variant="toggle"
            pressed={filter === 'comments'}
            onClick={() => setFilter('comments')}
          >
            {t('boardAdmin.activity.filter.comments')}
          </Button>
        </div>
      )}

      {/*
        ⚠⚠ FIRST, BEFORE THE ERROR ARM, AND THE ORDER IS LOAD-BEARING. The
        query is disabled while signed out, so it is neither loading nor errored
        and `data` is simply `undefined`; ordered after the error arm this
        honest state renders as a red "Activity could not be loaded" — a
        failure notice for something that is not a failure — and its Retry would
        fire the 401 anyway, because `refetch()` bypasses `enabled` (see the
        header).

        ⚠ `tone="neutral"`, never `"error"`: `EmptyState`'s `'error'` tone
        renders `role="alert"`, and nothing here has gone wrong. Being signed
        out is a fact about the visitor, not a fault in the board.
      */}
      {isSignedOut ? (
        <EmptyState
          tone="neutral"
          size="inline"
          headingLevel={3}
          title={t('boardAdmin.activity.signInTitle')}
          body={t('boardAdmin.activity.signInBody')}
        />
      ) : sessionPending || activity.isLoading ? (
        <Skeleton shape="block" count={3} />
      ) : activity.isError || data === undefined ? (
        <Notice
          tone="bad"
          title={t('boardAdmin.activity.error.title')}
          body={t('boardAdmin.activity.error.body')}
          action={
            <Button size="sm" onClick={() => void activity.refetch()}>
              {t('boardAdmin.activity.error.retry')}
            </Button>
          }
        />
      ) : data.entries.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          headingLevel={3}
          title={
            filter === 'comments'
              ? t('boardAdmin.activity.empty.commentsTitle')
              : t('boardAdmin.activity.empty.activityTitle')
          }
          body={
            filter === 'comments'
              ? t('boardAdmin.activity.empty.commentsBody')
              : t('boardAdmin.activity.empty.activityBody')
          }
        />
      ) : (
        <>
          <div>
            {data.entries.map((entry) => (
              <ActivityEntryItem
                key={entry.id}
                entry={entry}
                showCard
                onOpenCard={onOpenCard}
                highlighted={highlightedTarget === activityAnchor(entry.id)}
                onCopyLink={(activityId) => {
                  void copyActivityLink(activityId);
                }}
              />
            ))}
          </div>
          {data.nextCursor !== null ? (
            <button
              type="button"
              className="self-start rounded-card px-2 py-1 text-sm font-medium text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore
                ? t('boardAdmin.activity.loadingMore')
                : t('boardAdmin.activity.loadMore')}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
